package com.solevo.dist.pages.quotes

import androidx.compose.runtime.*
import com.solevo.dist.components.DashboardPageLayout
import com.solevo.dist.components.RequireAuth
import com.solevo.dist.components.WaitForAuthAndViewModel
import com.solevo.dist.navigation.NavigationConstants
import com.solevo.dist.viewmodels.QuoteDetailsViewModel
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color.Companion.rgb
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.icons.fa.FaChevronLeft
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.text.SpanText
import com.solevo.dist.i18n.LocalStrings
import com.solevo.dist.theme.styles.tableHeaderCell
import com.solevo.dist.theme.styles.tableBodyCell
import com.solevo.dist.theme.styles.tableRow
import com.solevo.dist.theme.styles.tableHeaderRow
import com.solevo.dist.theme.styles.secondaryText
import com.varabyte.kobweb.silk.components.graphics.Image
import com.solevo.dist.util.Res
import org.jetbrains.compose.web.css.*

@Page("/quotes/details")
@Composable
fun QuoteDetailsScreen() {
    RequireAuth { QuoteDetailsPage() }
}

@Composable
fun QuoteDetailsPage() {
    val context = rememberPageContext()
    val quoteId = context.route.params[NavigationConstants.QUOTE_ID_PARAM] ?: ""
    
    WaitForAuthAndViewModel(
        key = quoteId,
        viewModelFactory = { QuoteDetailsViewModel(quoteId) },
        onViewModelReady = { vm -> vm.start(quoteId) }
    ) { vm ->
        val ui by vm.state.collectAsState()

        DashboardPageLayout {
        Column(Modifier.fillMaxWidth().padding(24.px)) {
            Row(Modifier.fillMaxWidth().alignItems(AlignItems.Center).justifyContent(JustifyContent.Start)) {
                FaChevronLeft(
                    modifier = Modifier
                        .margin(top = 4.px)
                        .cursor(Cursor.Pointer)
                        .onClick { context.router.navigateTo("/quotes") },
                    size = IconSize.LG
                )
                val s = LocalStrings.current
                SpanText(
                    "${s.labelQuote} #${ui.quoteId}",
                    Modifier.margin(left = 12.px).fontSize(28.px).fontWeight(FontWeight.Bold)
                )
            }

            Box(Modifier.height(16.px))

            Column(Modifier.fillMaxWidth().gap(24.px)) {
                // 2x2 grid layout
                Column(
                    Modifier
                        .fillMaxWidth()
                        .display(DisplayStyle.Grid)
                        .styleModifier {
                            property("grid-template-columns", "repeat(3, 1fr)")
                            property("column-gap", "74px")
                            property("row-gap", "24px")
                            property("align-items", "center")
                        }
                ) {
                    val s2 = LocalStrings.current
                    DetailRow(title = s2.labelCustomerReference, value = ui.referenceText, iconSrc = Res.Icon.OrderIcons.reference)
                    DetailRow(title = s2.labelQuoteId, value = ui.orderId, iconSrc = Res.Icon.OrderIcons.numbers)
                    DetailRow(title = s2.labelStatus, value = ui.statusText, iconSrc = Res.Icon.OrderIcons.history)
                    val s3 = LocalStrings.current
                    DetailRow(title = s3.labelDateCreated, value = ui.createdText, iconSrc = Res.Icon.OrderIcons.calendar)
                    DetailRow(title = s3.labelPickupDate, value = ui.pickupText, iconSrc = Res.Icon.OrderIcons.calendar)
                }
            }

            Box(Modifier.height(24.px))

            Row(
                Modifier
                    .fillMaxWidth()
                    .display(DisplayStyle.Grid)
                    .styleModifier { property("grid-template-columns", "1.2fr 0.8fr 1fr 0.6fr 0.8fr") }
                    .tableHeaderRow()
            ) {
                val s4 = LocalStrings.current
                SpanText(s4.headerProduct, Modifier.tableHeaderCell())
                SpanText(s4.headerPricePerUnit, Modifier.tableHeaderCell())
                SpanText(s4.headerWarehouse, Modifier.tableHeaderCell())
                SpanText(s4.headerQuantity, Modifier.tableHeaderCell())
                SpanText(s4.headerTotal, Modifier.tableHeaderCell())
            }

            val listContext = rememberPageContext()
            ui.items.forEach { item ->
                Row(
                    Modifier
                        .fillMaxWidth()
                        .display(DisplayStyle.Grid)
                        .styleModifier { property("grid-template-columns", "1.2fr 0.8fr 1fr 0.6fr 0.8fr") }
                        .tableRow()
                        .cursor(Cursor.Pointer)
                        .onClick { listContext.router.navigateTo(com.solevo.dist.navigation.Screen.ProductDetailsPage.withId(item.productId)) }
                ) {
                    SpanText(item.name, Modifier.tableBodyCell())
                    SpanText(item.pricePerUnitText, Modifier.tableBodyCell())
                    SpanText(item.warehouse, Modifier.tableBodyCell())
                    SpanText(item.quantityText, Modifier.tableBodyCell())
                    SpanText(item.totalPriceText, Modifier.tableBodyCell())
                }
            }
        }
    }
    }
}

@Composable
private fun DetailRow(
    title: String,
    value: String,
    iconSrc: String,
    valueColor: com.varabyte.kobweb.compose.ui.graphics.Color? = null
) {
    Column(Modifier.fillMaxWidth()) {
        Row(
            Modifier
                .fillMaxWidth()
                .alignItems(AlignItems.Center)
                .padding(bottom = 12.px)
                .borderBottom(1.px, LineStyle.Solid, rgb(240, 242, 246))
                .styleModifier { property("min-height", "48px") }
        ) {
            Box(
                Modifier
                    .width(18.px)
                    .height(18.px)
                    .flexShrink(0)
                    .margin(right = 10.px)
            ) { Image(src = iconSrc, alt = "icon") }
            Column(Modifier.margin(left = 8.px).fillMaxWidth()) {
                SpanText(title, Modifier.secondaryText().styleModifier { property("white-space", "nowrap") })
                Box(Modifier.height(4.px))
                SpanText(
                    value.ifBlank { "—" },
                    Modifier
                        .tableBodyCell()
                        .then(if (valueColor != null) Modifier.color(valueColor) else Modifier)
                        .styleModifier { property("white-space", "nowrap") }
                )
            }
        }
    }
}



